import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Droplet, ArrowLeft, Search, FileText, FlaskConical, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/api";
import Seo from "@/components/Seo";

const FIELDS = [
  ["lot_number", "Lot Number"],
  ["product_sku", "Product SKU"],
  ["size", "Size"],
  ["manufacture_date", "Manufactured"],
  ["expiration_date", "Expires"],
  ["sterility_result", "Sterility"],
  ["sterility_method", "Sterility Method"],
  ["endotoxin_result", "Endotoxin"],
  ["ph_result", "pH"],
  ["benzyl_alcohol_percent", "Benzyl Alcohol %"],
  ["testing_lab", "Testing Lab"],
  ["report_number", "Report #"],
  ["report_date", "Report Date"],
];

const INCLUDES = [
  ["Sterility", "Confirmation that each lot meets sterility requirements for a research reagent, with the test method used."],
  ["Endotoxin", "Bacterial endotoxin level, verified to be within the acceptable research-grade limit."],
  ["pH", "Measured pH of the solution, confirming it falls within the specified range."],
  ["Benzyl Alcohol Content", "Verified concentration of the 0.9% benzyl alcohol bacteriostatic preservative."],
];

export default function LabResults() {
  const [lots, setLots] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/lots`)
      .then(({ data }) => setLots(data.lots || []))
      .catch(() => setLots([]));
  }, []);

  const currentLots = useMemo(
    () => (lots || []).filter((l) => l.status === "Current"),
    [lots]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return currentLots;
    return currentLots.filter(
      (l) =>
        (l.lot_number || "").toLowerCase().includes(q) ||
        (l.product_sku || "").toLowerCase().includes(q)
    );
  }, [currentLots, query]);

  return (
    <div className="min-h-screen bg-white" data-testid="lab-results-page">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="lab-results-logo">
            <span className="w-7 h-7 bg-slate-950 text-white flex items-center justify-center rounded-sm">
              <Droplet className="w-4 h-4" strokeWidth={2.2} />
            </span>
            <span className="font-chivo font-extrabold tracking-tight text-lg">
              LAB<span className="text-slate-400">2O</span>
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-500 hover:text-slate-950 transition-colors"
            data-testid="lab-results-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </div>
      </header>

      <Seo title="Lab Results & Certificates of Analysis | Lab2o" path="/lab-results" description="Look up the certificate of analysis for your lot: sterility, endotoxin, pH, and benzyl alcohol content." />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="font-plexmono text-[10px] tracking-[0.25em] uppercase text-slate-400 flex items-center gap-2" data-testid="lab-results-overline">
            <FlaskConical className="w-3.5 h-3.5" /> Quality documentation
          </p>
          <h1 className="mt-3 font-chivo font-extrabold tracking-tighter text-4xl sm:text-5xl text-slate-950" data-testid="lab-results-heading">
            Lab Results &amp; Certificates of Analysis
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed" data-testid="lab-results-subcopy">
            Every lot is tested before release; certificates are available on request. Enter the lot number printed on your box or packing slip
            to view its Certificate of Analysis.
          </p>
        </motion.div>

        <div className="mt-10 max-w-xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter the lot number printed on your box or packing slip"
              className="rounded-sm pl-11 h-12"
              data-testid="lab-results-search"
            />
          </div>
        </div>

        <div className="mt-12">
          {lots === null && (
            <p className="text-center text-slate-400 font-plexmono text-xs tracking-[0.2em] uppercase py-16" data-testid="lab-results-loading">
              Loading certificates…
            </p>
          )}

          {lots !== null && currentLots.length === 0 && (
            <div className="border border-slate-200 bg-slate-50 px-6 py-12 text-center" data-testid="lab-results-empty">
              <p className="text-slate-600 text-sm leading-relaxed max-w-md mx-auto">
                Certificates for current lots are being published. Contact us for the current COA.
              </p>
              <a
                href="mailto:orders@lab2o.com"
                className="mt-5 inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3 font-plexmono text-[11px] tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors"
                data-testid="lab-results-contact-button"
              >
                Contact us
              </a>
            </div>
          )}

          {lots !== null && currentLots.length > 0 && filtered.length === 0 && (
            <p className="text-center text-slate-500 text-sm py-12" data-testid="lab-results-no-match">
              No current lot matches that number. Check the lot number on your box or packing slip and try again.
            </p>
          )}

          {lots !== null && filtered.length > 0 && (
            <div className="border border-slate-200 overflow-x-auto" data-testid="lab-results-table">
              <table className="w-full text-sm min-w-[1100px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {FIELDS.map(([k, label]) => (
                      <th key={k} className="text-left px-4 py-3 font-plexmono text-[10px] tracking-[0.14em] uppercase text-slate-500 whitespace-nowrap">
                        {label}
                      </th>
                    ))}
                    <th className="text-left px-4 py-3 font-plexmono text-[10px] tracking-[0.14em] uppercase text-slate-500">COA</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lot) => (
                    <tr key={lot.lot_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`lab-results-row-${lot.lot_number}`}>
                      {FIELDS.map(([k]) => (
                        <td key={k} className="px-4 py-3.5 text-slate-700 whitespace-nowrap">
                          {k === "lot_number" ? (
                            <span className="font-plexmono font-medium text-slate-950">{lot[k] || "—"}</span>
                          ) : (
                            lot[k] || "—"
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {lot.coa_pdf_url ? (
                          <a
                            href={lot.coa_pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 border border-slate-950 px-3 py-1.5 font-plexmono text-[10px] tracking-[0.12em] uppercase hover:bg-slate-950 hover:text-white transition-colors"
                            data-testid={`lab-results-view-coa-${lot.lot_number}`}
                          >
                            <FileText className="w-3 h-3" /> View COA (PDF)
                          </a>
                        ) : (
                          <span className="text-slate-400 text-xs">On request</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-20" data-testid="lab-results-includes">
          <h2 className="font-chivo font-extrabold tracking-tight text-2xl text-slate-950">
            What each certificate includes
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {INCLUDES.map(([title, body], i) => (
              <div key={title} className="border border-slate-200 bg-white p-6" data-testid={`lab-results-include-${i}`}>
                <span className="font-plexmono text-[10px] tracking-[0.2em] text-slate-400 block mb-2">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-chivo font-bold text-slate-950">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 border-l-2 border-red-600 bg-red-50/60 px-5 py-4 flex items-start gap-3" data-testid="lab-results-disclaimer">
          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <p className="text-red-700 text-sm font-medium leading-snug">
            For research purposes only. Not for human or veterinary use or consumption.
          </p>
        </div>
      </main>
    </div>
  );
}
